'use client'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { ReactElement } from 'react'

import { cn } from '@/utils'

export const toggleGroupCls = cva(
	'flex h-10 items-center justify-center gap-1 rounded-full border px-[0.1875rem]',
	{
		variants: {
			visual: {
				primary: 'border-blue',
				secondary: 'border-border-grey',
			},
		},
	},
)

export type TSwitchVariantProps = VariantProps<typeof toggleGroupCls>

const ToggleGroup = React.forwardRef<
	React.ElementRef<typeof ToggleGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
		Required<TSwitchVariantProps>
>(({ visual, className, children, ...props }, ref) => (
	<ToggleGroupPrimitive.Root
		ref={ref}
		className={cn(toggleGroupCls({ visual }), className)}
		{...props}
	>
		{children}
	</ToggleGroupPrimitive.Root>
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

export const toggleGroupItemCls = cva(
	'type-body-small flex h-8 grow items-center justify-center gap-1 whitespace-nowrap rounded-full border bg-transparent px-4 font-medium transition [&_svg]:size-4',
	{
		variants: {
			visual: {
				primary:
					'border-transparent text-blue disabled:text-grey data-[state=on]:bg-blue data-[state=on]:text-white',
				secondary:
					'text-grey data-[state=off]:border-transparent data-[state=on]:border-border-grey data-[state=on]:bg-generator-bg data-[state=on]:text-black',
			},
		},
	},
)

const ToggleGroupItem = React.forwardRef<
	React.ElementRef<typeof ToggleGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
		Required<TSwitchVariantProps> & { equalWidthButtons?: boolean }
>(({ visual, equalWidthButtons, className, children, ...props }, ref) => {
	return (
		<ToggleGroupPrimitive.Item
			ref={ref}
			{...props}
			className={cn(
				toggleGroupItemCls({ visual }),
				equalWidthButtons && 'basis-0',
				className,
			)}
		>
			{children}
		</ToggleGroupPrimitive.Item>
	)
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export type TSwitchItem = {
	value: string
	label: string
	icon?: ReactElement
	disabled?: boolean
}

const BaseSwitch = ({
	visual,
	value,
	onValueChange,
	itemList,
	equalWidthButtons,
	className,
	toggleItemCls,
}: {
	itemList: TSwitchItem[]
	equalWidthButtons?: boolean
	className?: string
	toggleItemCls?: string
} & Pick<
	ToggleGroupPrimitive.ToggleGroupSingleProps,
	'value' | 'onValueChange'
> &
	Required<TSwitchVariantProps>) => (
	<ToggleGroup
		visual={visual}
		value={value}
		onValueChange={(value) => {
			if (value && onValueChange) {
				onValueChange(value)
			}
		}}
		type="single"
		className={className}
	>
		{itemList.map((item) => (
			<ToggleGroupItem
				key={item.value}
				visual={visual}
				value={item.value}
				equalWidthButtons={equalWidthButtons}
				disabled={item.disabled}
				className={toggleItemCls}
			>
				{item.icon}
				{item.label}
			</ToggleGroupItem>
		))}
	</ToggleGroup>
)

export default BaseSwitch
